import axios, { getData, isAxiosError } from "../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import {
  createErrorReportingAsyncThunk,
  defaultErrorAction,
} from "../../helpers";
import {
  RouteForLiveView,
  VehicleForLiveView,
  liveViewOverviewT,
  routeForLiveViewT,
} from "./types";
import { FlespiMessage, flespiMessagesT } from "dora-contracts";
import * as tPromise from "io-ts-promise";
import { DateTime } from "luxon";
import { AppThunkAction } from "../../../redux-store";
import { userStorage } from "../../../helpers/user-storage";

// Saved per user and restored once the user is known (see checkAuthState).
export const LIVE_VIEW_FILTERED_TEAMS_FILTER = "liveViewFilteredTeamsFilter";

type InitialState = {
  vehiclesForLiveView: VehicleForLiveView[];
  // Routes the server left out of the overview because their data is broken.
  unavailableRouteIds: string[];
  routeForLiveView: RouteForLiveView | null;
  // The route's trip has ended (live-view-route answers 404 for it).
  routeForLiveViewEnded: boolean;
  routeForLiveViewFlespiMessages: FlespiMessage[];
  filterTeamIds: string[];
};

const prefix = "data/live-view";

const initialState: InitialState = {
  routeForLiveViewFlespiMessages: [],
  vehiclesForLiveView: [],
  unavailableRouteIds: [],
  routeForLiveView: null,
  routeForLiveViewEnded: false,
  filterTeamIds: [],
};

export const loadVehiclesForLiveView = createErrorReportingAsyncThunk(
  `${prefix}/load-vehicles`,
  async (_: void, thunkApi) => {
    const overview = await axios
      .get("/api/tracking/live-view/vehicles")
      .then(getData)
      .then(tPromise.decode(liveViewOverviewT));
    // The server has already reported these; the user is told once per
    // route, not on every refetch.
    const previouslyUnavailable =
      thunkApi.getState().data.liveView.unavailableRouteIds;
    if (
      overview.unavailableRouteIds.some(
        (id) => !previouslyUnavailable.includes(id)
      )
    ) {
      thunkApi.dispatch(defaultErrorAction());
    }
    return overview;
  }
);

export const loadFlespiMessagesForDevice = createErrorReportingAsyncThunk(
  `${prefix}/load-flespi-messages`,
  async (params: {
    trackerDeviceId: string;
    startDate: string;
    endDate: string;
  }) => {
    const { trackerDeviceId, startDate, endDate } = params;
    return axios
      .get(`/api/tracker-devices/${trackerDeviceId}/messages`, {
        params: {
          startDate,
          endDate,
        },
      })
      .then(getData)
      .then(tPromise.decode(flespiMessagesT));
  }
);

export const loadVehicleRouteForLiveView = createErrorReportingAsyncThunk(
  `${prefix}/load-route`,
  async (routeId: string): Promise<RouteForLiveView | null> => {
    try {
      return await axios
        .get(`/api/tracking/live-view/vehicles/${routeId}`)
        .then(getData)
        .then(tPromise.decode(routeForLiveViewT));
    } catch (err) {
      // No driver is on the route (any more): not an error to report.
      if (isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  }
);

export const toggleFilterTeamIdAndSave =
  (payload: string): AppThunkAction =>
  (dispatch, getState) => {
    dispatch(liveViewSlice.actions.toggleFilterTeamId(payload));
    const { filterTeamIds } = getState().data.liveView;
    userStorage.setItem(
      LIVE_VIEW_FILTERED_TEAMS_FILTER,
      JSON.stringify(filterTeamIds)
    );
  };

const liveViewSlice = createSlice({
  name: "liveView",
  initialState,
  reducers: {
    toggleFilterTeamId: (state, action) => {
      const teamId = action.payload;
      if (state.filterTeamIds.includes(teamId)) {
        state.filterTeamIds = state.filterTeamIds.filter((id) => id !== teamId);
      } else {
        state.filterTeamIds.push(teamId);
      }
    },
    setSelectedTeams: (state, action) => {
      state.filterTeamIds = action.payload;
    },
    unloadRouteForLiveView: (state) => {
      state.routeForLiveView = null;
      state.routeForLiveViewEnded = false;
      state.routeForLiveViewFlespiMessages = [];
    },
    flespiTelemetryMessageReceivedForLiveViewRouteTruck: (state, action) => {
      const message = action.payload.message;
      state.routeForLiveViewFlespiMessages = [
        ...state.routeForLiveViewFlespiMessages,
        {
          timestamp: DateTime.utc().toMillis(),
          "position.latitude": message.lat,
          "position.longitude": message.lon,
          "position.speed": null,
          "engine.ignition.status": null,
          stopTimeInSeconds: undefined,
          "can.fuel.consumption": null,
        },
      ];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadVehiclesForLiveView.fulfilled, (state, action) => {
        state.vehiclesForLiveView = action.payload.vehicles;
        state.unavailableRouteIds = action.payload.unavailableRouteIds;
      })
      .addCase(loadVehicleRouteForLiveView.fulfilled, (state, action) => {
        if (action.payload) {
          state.routeForLiveView = action.payload;
        } else {
          state.routeForLiveViewEnded = true;
        }
      })
      .addCase(loadFlespiMessagesForDevice.fulfilled, (state, action) => {
        state.routeForLiveViewFlespiMessages = [
          ...state.routeForLiveViewFlespiMessages,
          ...action.payload,
        ];
      });
  },
});

export default liveViewSlice.reducer;

export const {
  unloadRouteForLiveView,
  flespiTelemetryMessageReceivedForLiveViewRouteTruck,
  setSelectedTeams,
} = liveViewSlice.actions;
